/**
 * savingsLabel.ts
 *
 * Pure utility functions for deriving customer-facing labels for coupons
 * and discount fees in TEK TIDES LABS.
 *
 * Rules:
 * - WELCOME25 (case-insensitive) -> "25% first-order savings"
 * - Quantity-savings code (tek-quantity-savings, case-insensitive, tolerant of underscores/spaces)
 *   -> percent = round(100 * couponDiscountMinor / preDiscountItemsSubtotalMinor)
 *   If percent is within 1 point of a tier in PACK_TIERS (10%, 15%, 20%), snap to that tier.
 *   Otherwise fall back to "Quantity savings".
 *   Label format: `${n}% quantity savings`.
 * - Other coupons keep existing display (code uppercased).
 * - Fees matching /tek[-_ ]?quantity[-_ ]?savings/i or existing quantity savings/discount labels
 *   get the same quantity label using |fee amount| vs subtotal.
 *
 * Pure, no React. No pricing or discount calculations are performed.
 */

import { PACK_TIERS } from "@/lib/packPricing";

export interface SavingsLabelCartContext {
  preDiscountItemsSubtotalMinor?: number;
  totalItemsMinor?: number;
  totalDiscountMinor?: number;
}

export interface CouponLike {
  code: string;
  discountMinor?: number;
  discountType?: string;
}

export interface FeeLike {
  id?: string;
  name: string;
  amountMinor?: number;
}

const QUANTITY_SAVINGS_CODE_REGEX = /^tek[-_ ]?quantity[-_ ]?savings$/i;
const QUANTITY_SAVINGS_FEE_REGEX = /tek[-_ ]?quantity[-_ ]?savings|quantity[-_ ]?(?:savings|discount)|quantity\s*tier/i;
const WELCOME25_REGEX = /^welcome25$/i;

/**
 * Checks whether a coupon code corresponds to the quantity savings promotion.
 */
export function isQuantitySavingsCode(code: string | undefined | null): boolean {
  if (!code) return false;
  return QUANTITY_SAVINGS_CODE_REGEX.test(code.trim());
}

/**
 * Checks whether a coupon code is the first-order promo code.
 */
export function isWelcome25Code(code: string | undefined | null): boolean {
  if (!code) return false;
  return WELCOME25_REGEX.test(code.trim());
}

/**
 * Snaps a computed percentage to the nearest known tier from PACK_TIERS
 * if within 1 percentage point (e.g. 9%-11% -> 10%, 14%-16% -> 15%, 19%-21% -> 20%).
 * Returns null if no tier matches.
 */
export function matchPackTierPercent(rawPercent: number): number | null {
  for (const tier of PACK_TIERS) {
    if (Math.abs(rawPercent - tier.discountPercent) <= 1) {
      return tier.discountPercent;
    }
  }
  return null;
}

/**
 * Derives the percentage quantity savings label from the discount amount and pre-discount subtotal.
 */
export function formatQuantitySavingsPercentLabel(
  discountMinor: number,
  subtotalMinor?: number
): string {
  const absDiscount = Math.abs(discountMinor);
  if (!subtotalMinor || subtotalMinor <= 0 || absDiscount <= 0) {
    return "Quantity savings";
  }

  const rawPercent = Math.round((100 * absDiscount) / subtotalMinor);
  const matchedPercent = matchPackTierPercent(rawPercent);

  if (matchedPercent !== null) {
    return `${matchedPercent}% quantity savings`;
  }

  if (rawPercent > 0 && rawPercent <= 100) {
    return `${rawPercent}% quantity savings`;
  }

  return "Quantity savings";
}

/**
 * Derives a customer-friendly display label for a coupon.
 *
 * - "welcome25" -> "25% first-order savings"
 * - "tek-quantity-savings" -> "10% quantity savings" / "15% quantity savings" / "20% quantity savings" (or "Quantity savings")
 * - Others -> uppercase code
 */
export function getCouponLabel(
  coupon: CouponLike,
  ctx?: SavingsLabelCartContext
): string {
  const code = (coupon.code || "").trim();
  if (!code) return "";

  if (isWelcome25Code(code)) {
    return "25% first-order savings";
  }

  if (isQuantitySavingsCode(code)) {
    const discountMinor = coupon.discountMinor ?? 0;
    const subtotalMinor =
      ctx?.preDiscountItemsSubtotalMinor ?? ctx?.totalItemsMinor ?? 0;
    return formatQuantitySavingsPercentLabel(discountMinor, subtotalMinor);
  }

  return code.toUpperCase();
}

/**
 * Derives a customer-friendly display label for a fee line item (e.g. negative discount fee).
 */
export function getFeeLabel(
  fee: FeeLike,
  ctx?: SavingsLabelCartContext
): string {
  const name = (fee.name || "").trim();
  if (!name) return "";

  if (QUANTITY_SAVINGS_FEE_REGEX.test(name)) {
    const discountMinor = Math.abs(fee.amountMinor ?? 0);
    const subtotalMinor =
      ctx?.preDiscountItemsSubtotalMinor ?? ctx?.totalItemsMinor ?? 0;
    return formatQuantitySavingsPercentLabel(discountMinor, subtotalMinor);
  }

  return name;
}
